import { useEffect, useState, useRef, useMemo } from "react";
import "../../assets/chatbotconfig.css"
import { addcontactus, InsertReservation, InsertSubIntent, saveMandetoryInfo } from "../../Services/chatbot";
import { toast } from "react-toastify";
import { ChevronDown, Mail, MessageCircle, Phone, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { HelpTip, labelClass } from "@/components/admin/cards";



const ReservationConfig = ({ contactRequired = true, contactEmailRequired = false, inputRefs, cancelJson, setCancelJson, setButtonName, agentJson, setAgentJson, setShowInfoImage, showInfoImage, setIsDirty, isGroupProp, setIsGroupProp, backupGroupProperty, setBackupGroupProperty, formJson, setFormJson, setcontactJson, loading, setLoading, formErrors, setFormErrors, groupData, isGroup, contactJson, loadInitialData }: any) => {

    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [searchText, setSearchText] = useState("");
    const dropdownRef = useRef<any>(null);

    const allProperties = useMemo(() => {
        if (!groupData) return [];
        return Object.keys(groupData).map((propertyId) => {
            const property = groupData[propertyId]?.PropertyData;
            return {
                property_id: property?.PropertyId || "",
                property_name: property?.PropertyName || "",
            };
        });
    }, [groupData]);

    const filteredProperties = useMemo(() => {
        if (!searchText.trim()) return allProperties;
        const lower = searchText.toLowerCase();
        return allProperties.filter((p) =>
            p.property_name.toLowerCase().includes(lower) ||
            p.property_id.toLowerCase().includes(lower)
        );
    }, [allProperties, searchText]);

    const selectedIds = useMemo(() => {
        return new Set((formJson.group_property || []).map((p) => p.property_id));
    }, [formJson.group_property]);

    const isAllSelected = allProperties.length > 0 && allProperties.every((p) => selectedIds.has(p.property_id));

    const toggleProperty = (prop) => {
        const exists = selectedIds.has(prop.property_id);
        let updated;
        if (exists) {
            updated = formJson.group_property.filter((p) => p.property_id !== prop.property_id);
        } else {
            updated = [...formJson.group_property, { property_id: prop.property_id, property_name: prop.property_name }];
        }
        setFormJson({ ...formJson, group_property: updated, property_type: updated.length > 0 ? "G" : "P" });
        setIsGroupProp(updated.length > 0);
        setIsDirty(true);
    };

    const toggleSelectAll = () => {
        let updated;
        if (isAllSelected) {
            updated = [];
        } else {
            updated = allProperties.map((p) => ({ property_id: p.property_id, property_name: p.property_name }));
        }
        setFormJson({ ...formJson, group_property: updated, property_type: updated.length > 0 ? "G" : "P" });
        setIsGroupProp(updated.length > 0);
        setIsDirty(true);
    };

    const clearAll = (e) => {
        e.stopPropagation();
        setFormJson({ ...formJson, group_property: [], property_type: "P" });
        setIsGroupProp(false);
        setIsDirty(true);
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setDropdownOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const handleCheckbox = (e) => {
        const isChecked = e.target.checked;
        setIsGroupProp(isChecked);

        setFormJson((prev) => {
            let updatedGroupProp = prev.group_property;

            if (isChecked) {
                const fromGroup = backupGroupProperty.length > 0
                    ? backupGroupProperty
                    : Object.keys(groupData || {}).map((propertyId) => {
                        const property = groupData[propertyId]?.PropertyData;
                        return {
                            property_id: property?.PropertyId || "",
                            property_name: property?.PropertyName || "",
                        };
                    });
                updatedGroupProp = fromGroup;
            } else {
                setBackupGroupProperty(formJson.group_property);
                updatedGroupProp = [];
            }


            return {
                ...prev,
                property_type: isChecked ? "G" : "P",
                group_property: updatedGroupProp,
            };
        });
        setIsDirty(true);
    };



    const handleInputChange = (index, field, value) => {
        setFormJson(prev => {
            const updatedGroupProperty = prev.group_property.map((item, i) =>
                i === index ? { ...item, [field]: value } : item
            );
            return { ...prev, group_property: updatedGroupProperty };
        });

    };

    const addProperty = () => {
        setFormJson({
            ...formJson,
            group_property: [
                ...formJson.group_property,
                { property_id: "", property_name: "" },
            ],
        });
    };

    const removeProperty = (index) => {
        const updated = [...formJson.group_property];
        updated.splice(index, 1);
        setFormJson({ ...formJson, group_property: updated });
    };


    const handleContactInputChange = (field, value) => {
        setcontactJson(prev => ({
            ...prev,
            [field]: value
        }));
        setIsDirty(true);
    };

    const handleContentChange = (field, value) => {
        setcontactJson(prev => ({
            ...prev,
            content: {
                ...prev.content,
                [field]: value
            }
        }));
        setIsDirty(true);
    };

    const req = <span className="text-destructive" aria-hidden="true"> *</span>;
    const errorText = (message) => message ? <p className="text-sm text-destructive">{message}</p> : null;
    const iconInput = (icon, input) => (
        <div className="relative">
            <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-muted-foreground [&_svg]:size-4" aria-hidden="true">{icon}</span>
            {input}
        </div>
    );

    return (
        <div className="flex flex-col gap-3 border-t border-border pt-3">
            <div className="flex items-center gap-1">
                <p className="text-sm font-semibold">Contact details</p>
                <HelpTip label="Contact details">
                    Shown by the Contact and Talk to an Agent buttons.
                    {contactRequired ? " Required while the Contact button is on." : ""}
                </HelpTip>
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
                <div className="flex min-w-0 flex-col gap-1.5">
                    <Label htmlFor="contactAddress" className={labelClass}>Address{contactRequired && req}</Label>
                    <Textarea
                        id="contactAddress"
                        placeholder="Enter full address"
                        rows={2}
                        value={contactJson.content.address}
                        ref={inputRefs.address}
                        onChange={(e) => {
                            handleContentChange('address', e.target.value)
                            if (e.target.value.trim()) {
                                setFormErrors((prev) => ({ ...prev, address: "" }));
                            }
                        }}
                    />
                    {errorText(formErrors.address)}
                </div>
                <div className="flex min-w-0 flex-col gap-1.5">
                    <Label htmlFor="contactEmail" className={labelClass}>Email{(contactRequired || contactEmailRequired) && req}</Label>
                    {iconInput(<Mail />, <Input
                        id="contactEmail"
                        type="email"
                        className="pl-9"
                        placeholder="Enter email address"
                        value={contactJson.content.email}
                        ref={inputRefs.email}
                        onChange={(e) => {
                            handleContentChange('email', e.target.value);
                            if (e.target.value.trim()) {
                                setFormErrors((prev) => ({ ...prev, email: "" }));
                            }
                        }}
                    />)}
                    {errorText(formErrors.email)}
                </div>
                <div className="flex min-w-0 flex-col gap-1.5">
                    <Label htmlFor="contactNumber" className={labelClass}>Contact number{contactRequired && req}</Label>
                    {iconInput(<Phone />, <Input
                        id="contactNumber"
                        type="tel"
                        className="pl-9"
                        placeholder="Enter contact number"
                        value={contactJson.content.contactNo}
                        ref={inputRefs.contactNo}
                        onChange={(e) => {
                            let val = e.target.value;

                            // ❌ Remove spaces
                            val = val.replace(/\s+/g, "");

                            // ✅ Allow only digits and one leading +
                            val = val.replace(/(?!^\+)[^\d]/g, "");

                            // ✅ Prevent multiple +
                            if (val.indexOf("+") > 0) {
                                val = val.replace(/\+/g, "");
                            }

                            // ✅ Restrict length: max 15 digits (excluding +)
                            let digitsOnly = val.replace(/\D/g, "");
                            if (digitsOnly.length > 15) {
                                digitsOnly = digitsOnly.slice(0, 15);
                            }
                            val = val.startsWith("+") ? "+" + digitsOnly : digitsOnly;

                            handleContentChange("contactNo", val);
                            // Clear error when user types
                            if (val.trim()) {
                                setFormErrors((prev) => ({ ...prev, contactNo: "" }));
                            }
                        }}
                    />)}
                    {errorText(formErrors.contactNo)}
                </div>
                <div className="flex min-w-0 flex-col gap-1.5">
                    <Label htmlFor="contactWhatsapp" className={labelClass}>WhatsApp number</Label>
                    {iconInput(<MessageCircle />, <Input
                        id="contactWhatsapp"
                        type="tel"
                        className="pl-9"
                        placeholder="Enter WhatsApp number"
                        value={contactJson.content.whatsappNo || ""}
                        ref={inputRefs.whatsappNo}
                        onChange={(e) => {
                            let val = e.target.value;

                            // ❌ Remove spaces
                            val = val.replace(/\s+/g, "");

                            // ✅ Allow only digits and one leading +
                            val = val.replace(/(?!^\+)[^\d]/g, "");

                            // ✅ Prevent multiple +
                            if (val.indexOf("+") > 0) {
                                val = val.replace(/\+/g, "");
                            }

                            // ✅ Restrict length: max 15 digits (excluding +)
                            let digitsOnly = val.replace(/\D/g, "");
                            if (digitsOnly.length > 15) {
                                digitsOnly = digitsOnly.slice(0, 15);
                            }
                            val = val.startsWith("+") ? "+" + digitsOnly : digitsOnly;

                            handleContentChange("whatsappNo", val);
                            // Clear error when user types
                            if (val.trim()) {
                                setFormErrors((prev) => ({ ...prev, whatsappNo: "" }));
                            }
                        }}
                    />)}
                    {errorText(formErrors.whatsappNo)}
                </div>
            </div>

            {isGroup && allProperties.length > 0 && (
                <div className="flex flex-col gap-1.5 sm:max-w-sm">
                    <span className={labelClass} id="groupPropertiesLabel">Group Properties</span>
                    <div className="relative" ref={dropdownRef}>
                        <button
                            type="button"
                            aria-labelledby="groupPropertiesLabel"
                            aria-expanded={dropdownOpen}
                            className="flex h-9 w-full items-center justify-between rounded-md border border-input bg-transparent px-3 text-sm shadow-xs dark:bg-input/30"
                            onClick={() => setDropdownOpen(!dropdownOpen)}
                        >
                            <span>
                                {formJson.group_property.length > 0
                                    ? `${formJson.group_property.length} Selected`
                                    : "Select Properties"}
                            </span>
                            <ChevronDown className="size-4 text-muted-foreground" aria-hidden="true" />
                        </button>
                        {formJson.group_property.length > 0 && (
                            <button type="button" aria-label="Clear selected properties" onClick={clearAll}
                                className="absolute top-1/2 right-8 -translate-y-1/2 rounded p-0.5 text-muted-foreground hover:bg-accent">
                                <X className="size-3.5" aria-hidden="true" />
                            </button>
                        )}
                        {dropdownOpen && (
                            <div className="absolute z-20 mt-1 w-full rounded-md border border-border bg-popover text-popover-foreground shadow-md">
                                <div className="border-b border-border p-2">
                                    <Input
                                        aria-label="Search properties"
                                        className="h-8"
                                        placeholder="Search"
                                        value={searchText}
                                        onChange={(e) => setSearchText(e.target.value)}
                                        onClick={(e) => e.stopPropagation()}
                                    />
                                </div>
                                <div className="max-h-60 overflow-y-auto p-1">
                                    {!searchText.trim() && (
                                        <label className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm font-semibold hover:bg-accent">
                                            <input type="checkbox" className="size-4 accent-primary" checked={isAllSelected} onChange={toggleSelectAll} />
                                            Select All
                                        </label>
                                    )}
                                    {filteredProperties.map((prop) => (
                                        <label key={prop.property_id} className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm hover:bg-accent">
                                            <input type="checkbox" className="size-4 accent-primary"
                                                checked={selectedIds.has(prop.property_id)} onChange={() => toggleProperty(prop)} />
                                            {prop.property_name}
                                        </label>
                                    ))}
                                    {filteredProperties.length === 0 && (
                                        <p className="px-2 py-1.5 text-sm text-muted-foreground">No properties found</p>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default ReservationConfig;
